import { useMutation } from '@tanstack/react-query';
import { useEffect, useRef } from 'preact/hooks';

import { patchUserMutationFn } from '@/queries/users';
import { useSelector } from '@/redux/helper';
import { selectTourGate, selectToursReady } from '@/redux/slices/tours/selectors';

import toursSyncPayload, { toursSyncDelta } from '../persistence/sync';
import { TourAnalytics, TourAnalyticsPatch, TourGateState } from '../types';

/** Coalesce bursts of step advances into a single request. */
const SYNC_DEBOUNCE_MS = 2000;

/**
 * Pushes durable tour analytics to the user object. Mounted once, globally (outside the
 * Router) — TourHost renders in more than one place, so syncing from there would double-send.
 *
 * Only the analytics travel; the step history stays local. Only the fields that actually
 * changed travel, so idle navigation costs no requests and a restart's clears are explicit.
 *
 * Requests are serialized through a promise chain so an older in-flight PATCH cannot overwrite
 * a newer one (or move the local baseline backwards) when the user advances quickly.
 */
const TourSync = () => {
  // Nothing is uploaded until the session resolves. Two reasons, and neither is optional:
  // `user.tours` only arrives with the session, and until it does the merged view below is
  // client-only — so an early send would push this device's copy over a fresher server record.
  const isReady = useSelector(selectToursReady);
  // The MERGED view, not the raw client slice: its monotonic guards are what make a sync
  // incapable of moving server-side progress backwards.
  const gate = useSelector(selectTourGate);
  const serverTours = useSelector((state) => state.user.tours);

  const { mutateAsync } = useMutation({
    mutationFn: (tours: Record<string, TourAnalyticsPatch>) => patchUserMutationFn({ tours }),
  });

  // What the server is believed to hold. Seeded from `user.tours` once the session lands, and
  // advanced only by a confirmed PATCH — so a failed send retries on the next change, and a
  // successful one is never re-sent.
  const baselineRef = useRef<Record<string, TourAnalytics> | undefined>(undefined);
  const gateRef = useRef<TourGateState>(gate);
  gateRef.current = gate;
  // Serialize PATCHes: each flush awaits the previous so out-of-order responses cannot regress.
  const chainRef = useRef(Promise.resolve());
  // Bumped when the session drops so an in-flight PATCH cannot restore the previous user's baseline.
  const generationRef = useRef(0);

  useEffect(() => {
    if (!isReady) {
      baselineRef.current = undefined; // a different user must not inherit this baseline
      generationRef.current += 1;
      return undefined;
    }
    if (!baselineRef.current) baselineRef.current = serverTours ?? {};

    const delta = toursSyncDelta(gate.tours, baselineRef.current);
    if (Object.keys(delta).length === 0) return undefined;

    const generation = generationRef.current;
    const timer = setTimeout(() => {
      chainRef.current = chainRef.current
        .catch(() => undefined) // keep the chain alive after a failure
        .then(async () => {
          if (generationRef.current !== generation) return;
          // Recompute against the latest gate + baseline at send time, not the debounce snapshot.
          const latest = gateRef.current;
          const nextDelta = toursSyncDelta(latest.tours, baselineRef.current);
          if (Object.keys(nextDelta).length === 0) return;
          const confirmed = toursSyncPayload(latest.tours);
          await mutateAsync(nextDelta);
          if (generationRef.current !== generation) return;
          baselineRef.current = confirmed;
        });
    }, SYNC_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [isReady, gate, serverTours, mutateAsync]);

  return null;
};

export default TourSync;
